import React, { useState, useMemo, useCallback } from 'react';
import { Box, Typography, Form, Loader, Row, Col, Button } from '@lucid/lego';
import { useStore } from '../../store/store';
import { createQuoteFormFields } from './formFields';
import type { TransformedCheckoutDetail } from '../../api/services/lupp/inventory/transformers.checkout';
import type { InventoryTableRow } from '../Configurator/utils';
import styles from './Checkout.module.scss';
import VehicleCard from './VehicleCard/VehicleCard';
import Docusign from './Docusign/Docusign';
import {
  useUpdateQuote,
  useCreateQuote,
  useDownloadQuote,
  useSendDocusign,
} from '../../api/services/lupp/quotes/quotes';
import type { SendDocusignFormData } from './Docusign/Docusign';
import CheckoutModal from './Modal/Modal';

const SUCCESS_MESSAGE = 'The quotation has been successfully saved to the prospect.';
const SUCCESS_UPDATE_MESSAGE = 'The quotation has been successfully updated to the prospect.';
const VALIDATION_ERROR = 'Please fix the form errors before submitting.';

/**
 * Quote form data type representing all form fields
 */
type QuoteFormData = {
  prospectType: string;
  preferredPaymentMethod: string;
  deliveryTimeline: Date | string;
  companyName?: string;
  companyTaxId?: string;
  invoiceAddress?: string;
  businessCity?: string;
  businessPostalCode?: string;
  financeCompanyName?: string;
  financeCompanyAddress?: string;
  financeCity?: string;
  financePostalCode?: string;
  financeIban?: string;
  requestedIncentives?: string[];
  cashDiscount?: string;
  percentageDiscount?: string;
};

/**
 * Prospect details for quote creation
 */
export type ProspectDetails = {
  prospectId: string;
  prospectName?: string;
  quoteId?: string;
};

/**
 * Props for the Checkout component
 */
export type CheckoutProps = {
  /**
   * The selected vehicle data
   */
  vehicle: InventoryTableRow;
  /**
   * Checkout detail data from the API (transformed)
   */
  checkoutData: TransformedCheckoutDetail | null;
  /**
   * Whether the checkout data is loading
   */
  isLoading: boolean;
  /**
   * Callback to go back to the inventory list
   */
  onBack: () => void;
  /**
   * Custom data-testid for the component
   * @default 'checkout'
   */
  dataTestId?: string;
  /**
   * Prospect details including ID, name and quote ID
   */
  prospectDetails?: ProspectDetails;
  /**
   * Current docusign status from quote API
   */
  docusignStatus?: string;
  /**
   * Current quote status from quote API
   */
  currentStatus?: string;
};

/**
 * Checkout component displays vehicle details and quote creation form
 * @component
 * @param props - Component props
 * @param props.vehicle - The selected vehicle data
 * @param props.checkoutData - Checkout detail data from the API
 * @param props.isLoading - Whether the checkout data is loading
 * @param props.onBack - Callback to go back to inventory
 * @param props.dataTestId - Test ID for the component
 * @param props.prospectDetails - Prospect details including ID, name and quote ID
 * @param props.docusignStatus - Current docusign status from API
 * @param props.currentStatus - Current quote status from API
 * @returns JSX.Element
 *
 * @example
 * <Checkout
 *   vehicle={selectedVehicle}
 *   checkoutData={checkoutDetails}
 *   isLoading={false}
 *   onBack={() => setSelectedVehicle(null)}
 *   prospectDetails={{ prospectId: '123', prospectName: 'John Doe' }}
 * />
 */
const Checkout: React.FC<CheckoutProps> = ({
  vehicle,
  checkoutData,
  isLoading,
  onBack,
  dataTestId = 'checkout',
  prospectDetails,
  docusignStatus: initialDocusignStatus,
  currentStatus,
}) => {
  const theme = useStore((state) => state.theme);

  // State declarations
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isQuoteSaved, setIsQuoteSaved] = useState(false);
  const [savedQuoteId, setSavedQuoteId] = useState<string | undefined>(prospectDetails?.quoteId);
  const [isDocusignModalOpen, setIsDocusignModalOpen] = useState(false);
  const [isSendingDocusignModalOpen, setIsSendingDocusignModalOpen] = useState(false);
  const [isDocusignSending, setIsDocusignSending] = useState(false);
  const [isDocusignSent, setIsDocusignSent] = useState(false);
  const [docusignStatus, setDocusignStatus] = useState<string | undefined>(initialDocusignStatus);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [successModalMessage, setSuccessModalMessage] = useState<string>('');

  const { mutate: updateQuote, isPending: isUpdatePending } = useUpdateQuote({
    prospectId: prospectDetails?.prospectId ?? '',
    quoteId: prospectDetails?.quoteId ?? '',
  });

  const { mutate: createQuote, isPending: isCreatePending } = useCreateQuote({
    prospectId: prospectDetails?.prospectId ?? '',
  });

  const { refetch: downloadQuote, isFetching: isDownloadPending } = useDownloadQuote({
    prospectId: prospectDetails?.prospectId ?? '',
    quoteId: savedQuoteId ?? '',
    enabled: false,
  });

  const { mutate: sendDocusign, isPending: isSendDocusignPending } = useSendDocusign({
    prospectId: prospectDetails?.prospectId ?? '',
    quoteId: savedQuoteId ?? '',
  });

  const { rightSideSummary } = checkoutData || {};

  const formFields = useMemo(
    () => createQuoteFormFields(rightSideSummary, { currentStatus, docusignStatus }),
    [rightSideSummary, currentStatus, docusignStatus]
  );

  /**
   * Handles successful form submission
   * @param {unknown} data - Form data from the submission
   */
  const handleSuccess = useCallback(
    (data: unknown) => {
      setErrorMessage(null);
      const formData = data as QuoteFormData;

      // Build payload based on form data
      const payload = {
        prospectType: formData.prospectType,
        preferredPaymentMethod: formData.preferredPaymentMethod,
        deliveryTimeline:
          formData.deliveryTimeline instanceof Date
            ? formData.deliveryTimeline.toISOString()
            : formData.deliveryTimeline,
        // Business information (conditional)
        ...(formData.prospectType === 'Business' && {
          companyName: formData.companyName,
          companyTaxIdentification: formData.companyTaxId,
          invoiceAddressLine1: formData.invoiceAddress,
          invoiceCity: formData.businessCity,
          invoicePostalCode: formData.businessPostalCode,
        }),
        // Finance provider details (conditional)
        ...(formData.preferredPaymentMethod === '3rd Party Financing' && {
          financeCompanyName: formData.financeCompanyName,
          financeCompanyAddress: formData.financeCompanyAddress,
          financeCity: formData.financeCity,
          financePostalCode: formData.financePostalCode,
          financeIban: formData.financeIban,
        }),
        // Incentives
        requestedIncentives: formData.requestedIncentives,
        cashDiscount: formData.cashDiscount,
        percentageDiscount: formData.percentageDiscount,
      };

      // Merge payload with checkoutData
      const mergedPayload = {
        ...payload,
        vehicleDetails: { ...checkoutData },
      };

      if (prospectDetails?.prospectId && prospectDetails?.quoteId) {
        updateQuote(mergedPayload, {
          onSuccess: (response) => {
            setSuccessModalMessage(SUCCESS_UPDATE_MESSAGE);
            setIsSuccessModalOpen(true);
            setIsQuoteSaved(true);
            if (response?.quoteIdentifier) {
              setSavedQuoteId(response.quoteIdentifier);
            }
          },
          onError: () => {
            setErrorMessage('Failed to update quote. Please try again.');
          },
        });
      } else {
        createQuote(mergedPayload, {
          onSuccess: (response) => {
            setSuccessModalMessage(SUCCESS_MESSAGE);
            setIsSuccessModalOpen(true);
            setIsQuoteSaved(true);
            if (response?.quoteIdentifier) {
              setSavedQuoteId(response.quoteIdentifier);
            }
          },
          onError: () => {
            setErrorMessage('Failed to create quote. Please try again.');
          },
        });
      }
    },
    [prospectDetails, checkoutData, updateQuote, createQuote]
  );

  /**
   * Handles form validation errors
   * @param {unknown} error - The validation error
   */
  const handleError = useCallback((_error: unknown) => {
    setErrorMessage(VALIDATION_ERROR);
  }, []);

  /**
   * Opens the Send Docusign modal
   */
  const handleClickOnSendDocusign = useCallback(() => {
    setIsDocusignModalOpen(true);
  }, []);

  /**
   * Closes the Send Docusign modal
   */
  const handleCloseDocusignModal = useCallback(() => {
    setIsDocusignModalOpen(false);
  }, []);

  /**
   * Closes the Sending Docusign modal
   */
  const handleCloseSendingDocusignModal = useCallback(() => {
    setIsSendingDocusignModalOpen(false);
    // Reset states after modal animation completes
    setTimeout(() => {
      setIsDocusignSending(false);
      setIsDocusignSent(false);
    }, 300);
  }, []);

  /**
   * Handles the Send Docusign form submission
   * @param data - Form data from the Send Docusign modal
   */
  const handleSendDocusign = useCallback(
    (data: SendDocusignFormData) => {
      // Close form modal and open sending modal
      setIsDocusignModalOpen(false);
      setIsSendingDocusignModalOpen(true);
      setIsDocusignSending(true);

      // Delay before making API call
      setTimeout(() => {
        sendDocusign(
          {
            firstName: data.firstName,
            lastName: data.lastName,
            email: data.email,
            placeVehicleOnHold: data.placeOnHold,
          },
          {
            onSuccess: (response) => {
              // Delay before showing sent state
              setTimeout(() => {
                setIsDocusignSending(false);
                setIsDocusignSent(true);
                // Use response status or default to 'Shared'
                setDocusignStatus(response?.status || 'Shared');
              }, 1500);
            },
            onError: () => {
              setIsDocusignSending(false);
              setIsSendingDocusignModalOpen(false);
              setErrorMessage('Failed to send Docusign. Please try again.');
            },
          }
        );
      }, 1000);
    },
    [sendDocusign]
  );

  /**
   * Handles downloading the quotation as a PDF
   */
  const handleDownloadQuotation = useCallback(async () => {
    const { data } = await downloadQuote();
    if (data) {
      const url = window.URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `quote-${savedQuoteId || 'download'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }
  }, [downloadQuote, savedQuoteId]);

  const isFormLoading =
    isUpdatePending || isCreatePending || isDownloadPending || isSendDocusignPending;

  const pageTitle = useMemo(
    () =>
      prospectDetails?.prospectName
        ? `Quotation for ${prospectDetails.prospectName}`
        : 'Vehicle Information',
    [prospectDetails?.prospectName]
  );

  // Show loader while initial data is loading
  if (isLoading) {
    return (
      <Box data-testid={dataTestId} className={styles.checkoutContainer}>
        <Box className={styles.loaderWrapper}>
          <Loader />
        </Box>
      </Box>
    );
  }

  // Show error state if no checkout data
  if (!checkoutData) {
    return (
      <Box data-testid={dataTestId} className={styles.checkoutContainer}>
        <Box className={styles.noDataContainer}>
          <Button
            type="tertiary"
            color="neutral"
            theme={theme}
            dataTestId="back-button"
            gaComponent="checkout"
            label="Back"
            iconLeft={{ category: 'arrows', name: 'arrow-left' }}
            handleClick={onBack}
          />
          <Typography variant="body" size="medium" color="gray-4" theme={theme}>
            {prospectDetails?.quoteId
              ? `No checkout data available for Quote ID: ${prospectDetails.quoteId}`
              : `No checkout data available for Vehicle VIN: ${vehicle.vin}`}
          </Typography>
        </Box>
      </Box>
    );
  }

  // Render the quote creation form
  return (
    <Box data-testid={dataTestId} className={styles.pageContainer}>
      <Typography variant="headline" size="small" color="primary-1" theme={theme}>
        {pageTitle}
      </Typography>
      <Box pt={8} mb={8}>
        <Typography variant="body" size="medium" color="gray-5" theme={theme}>
          Saved quotations can be reviewed in prospect details.
        </Typography>
      </Box>
      <Box className={styles.formContent} mt={32}>
        <Box className={styles.formWrapper}>
          {errorMessage && (
            <Box mb={16} pt={12} pb={12} pl={16} pr={16} className={styles.errorMessageText}>
              <Typography variant="body" size="medium" color="white" theme={theme}>
                {errorMessage}
              </Typography>
            </Box>
          )}
          {isFormLoading && (
            <Box className="fetchingOverlay">
              <Loader size={32} />
            </Box>
          )}

          <Button
            type="tertiary"
            color="neutral"
            theme="light"
            dataTestId="back-button"
            gaComponent="checkout"
            label="Back"
            iconLeft={{ category: 'arrows', name: 'arrow-left' }}
            handleClick={onBack}
          />
          <Row>
            <Col xs={12} md={6}>
              <VehicleCard
                theme={theme}
                checkoutData={checkoutData}
                onSendDocusign={handleClickOnSendDocusign}
                onDownloadQuotation={handleDownloadQuotation}
                quoteId={savedQuoteId}
                docusignStatus={docusignStatus}
                isQuoteSaved={isQuoteSaved}
                currentStatus={currentStatus}
              />
            </Col>
            <Col xs={12} md={6}>
              <Form
                id="create-quote-form"
                theme={theme === 'dark' ? 'light' : 'dark'}
                formFields={formFields}
                onSuccess={handleSuccess}
                onError={handleError}
                isFluid={true}
              />
            </Col>
          </Row>
        </Box>
      </Box>

      <Docusign
        dataTestId="send-docusign-modal"
        isOpen={isDocusignModalOpen}
        onClose={handleCloseDocusignModal}
        onSend={handleSendDocusign}
        theme={theme === 'light' ? 'dark' : 'light'}
      />

      <CheckoutModal
        dataTestId="sending-docusign-modal"
        isOpen={isSendingDocusignModalOpen}
        onClose={handleCloseSendingDocusignModal}
        theme="light"
        variant={isDocusignSent ? 'sent' : 'sending'}
        showLoader={isDocusignSending}
      />

      <CheckoutModal
        dataTestId="save-quote-modal"
        isOpen={isSuccessModalOpen}
        onClose={() => {
          setIsSuccessModalOpen(false);
        }}
        theme={theme === 'light' ? 'dark' : 'light'}
        variant="success"
        message={successModalMessage}
      />
    </Box>
  );
};

Checkout.displayName = 'Checkout';

export default Checkout;
